import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Edit, AlertTriangle, RefreshCw, Bot, FileSpreadsheet, Clock, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReviewQueuePage() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    type: 'all',
    source: 'all',
    status: 'pending_review'
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Modal states
  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [rejectModal, setRejectModal] = useState({ open: false, item: null });
  const [editedData, setEditedData] = useState({});
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  
  const getToken = () => localStorage.getItem('carbiq_admin_token');
  const getAdminEmail = () => {
    try {
      const token = getToken();
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub || 'admin@carbiq.app';
      }
    } catch {}
    return 'admin@carbiq.app';
  };
  
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/review-queue/stats`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);
  
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.type && filters.type !== 'all') params.append('type', filters.type);
      if (filters.source && filters.source !== 'all') params.append('source', filters.source);
      params.append('status', filters.status);
      params.append('page', page);
      params.append('limit', 20);
      
      const response = await fetch(`${API_URL}/api/v1/admin/review-queue?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setItems(data.items || []);
        setTotalPages(data.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);
  
  useEffect(() => {
    fetchStats();
    fetchItems();
  }, [fetchStats, fetchItems]);
  
  const approveItem = async (item) => {
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/review-queue/${item._id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({ adminEmail: getAdminEmail() })
      });
      
      if (response.ok) {
        toast.success(`${item.data?.name || item.data?.title} approved and published`);
        fetchItems();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Approval failed');
      }
    } catch (error) {
      toast.error('Approval failed');
    } finally {
      setProcessing(false);
    }
  };
  
  const approveWithEdits = async () => {
    if (!editModal.item) return;
    
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/review-queue/${editModal.item._id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          adminEmail: getAdminEmail(),
          editedData: { ...editModal.item.data, ...editedData }
        })
      });
      
      if (response.ok) {
        toast.success('Item approved with edits');
        setEditModal({ open: false, item: null });
        setEditedData({});
        fetchItems();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Approval failed');
      }
    } catch (error) {
      toast.error('Approval failed');
    } finally {
      setProcessing(false);
    }
  };
  
  const rejectItem = async () => {
    if (!rejectModal.item || !rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/review-queue/${rejectModal.item._id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          adminEmail: getAdminEmail(),
          reason: rejectReason
        })
      });
      
      if (response.ok) {
        toast.success('Item rejected');
        setRejectModal({ open: false, item: null });
        setRejectReason('');
        fetchItems();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Rejection failed');
      }
    } catch (error) {
      toast.error('Rejection failed');
    } finally {
      setProcessing(false);
    }
  };
  
  const bulkApprove = async () => {
    const pendingIds = items.filter(i => i.status === 'pending_review').map(i => i._id);
    if (pendingIds.length === 0) return;
    
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/review-queue/bulk-approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          itemIds: pendingIds,
          adminEmail: getAdminEmail()
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Approved ${data.approved} items`);
        fetchItems();
        fetchStats();
      }
    } catch (error) {
      toast.error('Bulk approve failed');
    } finally {
      setProcessing(false);
    }
  };
  
  const renderItemCard = (item) => {
    const data = item.data || {};
    const contentType = item.contentType;
    
    return (
      <Card key={item._id} className="mb-4">
        <CardContent className="pt-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* Header */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg font-semibold">{data.name || data.title}</span>
                <Badge variant={item.source === 'ai_generated' ? 'default' : 'secondary'}>
                  {item.source === 'ai_generated' ? (
                    <><Bot className="h-3 w-3 mr-1" /> AI</>
                  ) : (
                    <><FileSpreadsheet className="h-3 w-3 mr-1" /> CSV</>
                  )}
                </Badge>
                <Badge variant="outline">{contentType}</Badge>
                {data.glycemicIndex !== undefined && (
                  <Badge variant={data.glycemicIndex >= 70 ? 'destructive' : data.glycemicIndex >= 55 ? 'outline' : 'secondary'}>
                    GI: {data.glycemicIndex}
                  </Badge>
                )}
                {data.gi !== undefined && (
                  <Badge variant={data.gi >= 70 ? 'destructive' : data.gi >= 55 ? 'outline' : 'secondary'}>
                    GI: {data.gi}
                  </Badge>
                )}
              </div>
              
              {/* Content Preview */}
              <div className="text-sm text-muted-foreground space-y-1">
                {contentType === 'food' && (
                  <>
                    <p>Category: {data.category} | Carbs: {data.carbsPer100g}g | Sugar: {data.sugarPer100g}g | Fibre: {data.fibrePer100g}g</p>
                    <p>Protein: {data.proteinPer100g}g | Fat: {data.fatPer100g}g | Calories: {data.caloriesPer100g}</p>
                    {data.diabeticNotes && <p className="text-xs italic">{data.diabeticNotes}</p>}
                  </>
                )}
                {contentType === 'recipe' && (
                  <>
                    <p>Category: {data.category} | Prep: {data.prepTimeMinutes}min | Servings: {data.servings}</p>
                    <p>Ingredients: {data.ingredients?.length || 0} | Steps: {data.steps?.length || 0}</p>
                    {data.whyGood && <p className="text-xs italic">{data.whyGood}</p>}
                  </>
                )}
                {contentType === 'rescue_action' && (
                  <>
                    <p>Type: {data.actionType} | Timing: {data.timing} | Reduction: {data.estimatedReduction}</p>
                    <p className="text-xs">{data.description}</p>
                  </>
                )}
                {contentType === 'smart_combo' && (
                  <>
                    <p>Foods: {data.foods?.join(', ')}</p>
                    {data.whyGood && <p className="text-xs italic">{data.whyGood}</p>}
                  </>
                )}
              </div>
              
              {/* Warnings */}
              {(item.duplicateWarnings?.length > 0 || item.validationWarnings?.length > 0) && (
                <div className="mt-3 space-y-2">
                  {item.duplicateWarnings?.length > 0 && (
                    <div className="p-2 bg-amber-50 border border-amber-200 rounded text-sm">
                      <AlertTriangle className="h-4 w-4 inline mr-1 text-amber-600" />
                      <span className="text-amber-800">
                        Similar item exists: "{item.duplicateWarnings[0].existingName}" 
                        ({Math.round(item.duplicateWarnings[0].similarity * 100)}% match)
                      </span>
                    </div>
                  )}
                  {item.validationWarnings?.length > 0 && (
                    <div className="p-2 bg-red-50 border border-red-200 rounded text-sm">
                      <AlertTriangle className="h-4 w-4 inline mr-1 text-red-600" />
                      <span className="text-red-800">
                        Validation: {item.validationWarnings.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Meta */}
              <div className="mt-2 text-xs text-muted-foreground flex items-center gap-2">
                <Clock className="h-3 w-3" />
                Generated {new Date(item.generatedAt).toLocaleString()} by {item.generatedBy}
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex flex-col gap-2 ml-4">
              <Button
                size="sm"
                onClick={() => approveItem(item)}
                disabled={processing}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditModal({ open: true, item });
                  setEditedData(item.data || {});
                }}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit & Approve
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setRejectModal({ open: true, item })}
              >
                <XCircle className="h-4 w-4 mr-1" />
                Reject
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };
  
  return (
    <div className="space-y-6" data-testid="review-queue-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Review Queue</h1>
          <p className="text-muted-foreground">Approve or reject AI-generated and CSV-imported content</p>
        </div>
        <Button variant="outline" onClick={() => { fetchStats(); fetchItems(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-bold text-primary">{stats.pending}</div>
              <div className="text-sm text-muted-foreground">Pending Review</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-bold text-green-600">{stats.approvedToday}</div>
              <div className="text-sm text-muted-foreground">Approved Today</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-3xl font-bold text-red-600">{stats.rejectedTotal}</div>
              <div className="text-sm text-muted-foreground">Total Rejected</div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Label>Type:</Label>
              <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="food">Foods</SelectItem>
                  <SelectItem value="recipe">Recipes</SelectItem>
                  <SelectItem value="rescue_action">Rescue Actions</SelectItem>
                  <SelectItem value="smart_combo">Smart Combos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Label>Source:</Label>
              <Select value={filters.source} onValueChange={(v) => setFilters({ ...filters, source: v })}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="ai_generated">AI Generated</SelectItem>
                  <SelectItem value="csv_import">CSV Import</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1" />
            
            <Button
              variant="default"
              onClick={bulkApprove}
              disabled={processing || items.filter(i => i.status === 'pending_review').length === 0}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Approve All Visible ({items.filter(i => i.status === 'pending_review').length})
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Items List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
            <h3 className="text-lg font-semibold">All caught up!</h3>
            <p className="text-muted-foreground">No items awaiting review</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {items.map(renderItemCard)}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </Button>
              <span className="py-2 px-4">Page {page} of {totalPages}</span>
              <Button
                variant="outline"
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
      
      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, item: null })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit & Approve</DialogTitle>
            <DialogDescription>Make changes before approving</DialogDescription>
          </DialogHeader>
          
          {editModal.item && (
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={editedData.name || editedData.title || ''}
                  onChange={(e) => setEditedData({ 
                    ...editedData, 
                    [editModal.item.contentType === 'rescue_action' ? 'title' : 'name']: e.target.value 
                  })}
                />
              </div>
              
              {editModal.item.contentType === 'food' && (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Glycemic Index</Label>
                      <Input
                        type="number"
                        value={editedData.glycemicIndex || ''}
                        onChange={(e) => setEditedData({ ...editedData, glycemicIndex: parseInt(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label>Carbs per 100g</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={editedData.carbsPer100g || ''}
                        onChange={(e) => setEditedData({ ...editedData, carbsPer100g: parseFloat(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label>Sugar per 100g</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={editedData.sugarPer100g || ''}
                        onChange={(e) => setEditedData({ ...editedData, sugarPer100g: parseFloat(e.target.value) })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Diabetic Notes</Label>
                    <Textarea
                      value={editedData.diabeticNotes || ''}
                      onChange={(e) => setEditedData({ ...editedData, diabeticNotes: e.target.value })}
                    />
                  </div>
                </>
              )}
              
              {editModal.item.contentType === 'recipe' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>GI</Label>
                      <Input
                        type="number"
                        value={editedData.gi || ''}
                        onChange={(e) => setEditedData({ ...editedData, gi: parseInt(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label>Prep Time (minutes)</Label>
                      <Input
                        type="number"
                        value={editedData.prepTimeMinutes || ''}
                        onChange={(e) => setEditedData({ ...editedData, prepTimeMinutes: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Why Good for Diabetics</Label>
                    <Textarea
                      value={editedData.whyGood || ''}
                      onChange={(e) => setEditedData({ ...editedData, whyGood: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, item: null })}>
              Cancel
            </Button>
            <Button onClick={approveWithEdits} disabled={processing}>
              {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save & Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reject Modal */}
      <Dialog open={rejectModal.open} onOpenChange={(open) => !open && setRejectModal({ open: false, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Item</DialogTitle>
            <DialogDescription>
              Rejecting: {rejectModal.item?.data?.name || rejectModal.item?.data?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label>Reason for rejection</Label>
            <Textarea
              placeholder="Why is this item being rejected?"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectModal({ open: false, item: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={rejectItem} disabled={processing}>
              {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
